import { Routes } from '@angular/router';

import { APP_PATH } from './core/constants/app-paths';
import { membershipSessionGuard } from './core/guards/membership-session.guard';

/**
 * Five destinations, all lazy so a member who only wants to look at plans never
 * downloads the dashboard.
 *
 * /plans is the default because this page's first job is to explain what a
 * membership is; a member who already has one reaches /my-membership from the bar.
 */
export const APP_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: APP_PATH.PLANS },
  {
    path: APP_PATH.PLANS,
    loadChildren: () => import('./features/plans/plans.routes').then((m) => m.PLANS_ROUTES),
  },
  {
    path: APP_PATH.VERIFY,
    loadChildren: () => import('./features/verify/verify.routes').then((m) => m.VERIFY_ROUTES),
    data: { preloadPriority: 1 },
  },
  {
    path: APP_PATH.PURCHASE,
    loadChildren: () => import('./features/purchase/purchase.routes').then((m) => m.PURCHASE_ROUTES),
    // The next screen after choosing a plan: downloaded first (see IdlePreloadStrategy).
    data: { preloadPriority: 0 },
  },
  {
    path: APP_PATH.MY_MEMBERSHIP,
    canActivate: [membershipSessionGuard],
    loadChildren: () =>
      import('./features/my-membership/my-membership.routes').then((m) => m.MY_MEMBERSHIP_ROUTES),
  },
  {
    path: APP_PATH.PROFILE,
    canActivate: [membershipSessionGuard],
    loadChildren: () => import('./features/profile/profile.routes').then((m) => m.PROFILE_ROUTES),
  },
  /*
   * Refer to Friends & Family (Builder-Growth 6.2).
   *
   * ⚠ NOT behind the session guard any more (D-D8, owner 2026-09-18). The page's whole job is to
   * explain what referring gives you and then start it; sending a signed-out visitor to a sign-in
   * screen INSTEAD of the page is the "we ask for the OTP on every page" complaint. The page itself
   * renders the offer without a session and asks for one only when the member acts. Nothing
   * private leaks by being reachable: `GET /me/referral` still needs the token, so the link, the
   * history and the credit are fetched ONLY when there is a session.
   */
  {
    path: APP_PATH.REFER,
    loadChildren: () => import('./features/refer/refer.routes').then((m) => m.REFER_ROUTES),
  },
  /*
   * ADD 23 names `/membership?ref=` as the shared link. The server's link points at `/plans`; both
   * land on the plans page, and the redirect keeps the query string, so `?ref=` survives either way.
   */
  { path: APP_PATH.MEMBERSHIP, pathMatch: 'full', redirectTo: APP_PATH.PLANS },
  /*
   * Cashfree sends the patient back here (C1): `/membership-payment/return?order=…&ref=…`. NOT behind
   * the session guard: the tab may have lost its session, and the status is answered without one.
   */
  {
    path: APP_PATH.MEMBERSHIP_PAYMENT,
    loadChildren: () =>
      import('./features/payment-return/payment-return.routes').then((m) => m.PAYMENT_RETURN_ROUTES),
  },
  {
    path: APP_PATH.DOCTORS,
    loadChildren: () => import('./features/doctors/doctors.routes').then((m) => m.DOCTORS_ROUTES),
  },
  {
    path: APP_PATH.FIND_CARE,
    loadChildren: () => import('./features/find-care/find-care.routes').then((m) => m.FIND_CARE_ROUTES),
  },
  /*
   * THE LIFEMAAN REVIEW SYSTEM, at an address of its own (owner, 2026-09-24).
   *
   * ⚠ IT IS ADDRESSED BY THE PARTNER, NOT BY A PAGE. *"The page review is for partner whether they
   * publish or not. We do not care."* The Find care card used to send a member to the partner's
   * marketing page to rate them, so a partner who had never built one could not be rated from the
   * listing at all — which was one of the two real participating partners.
   *
   * ⚠ NOT BEHIND THE SESSION GUARD. Reading a rating needs nobody signed in, and the panel asks
   * for a one-time code only when the member actually rates — the same rule `/refer` follows, for
   * the same reason: sending a visitor to a sign-in screen INSTEAD of the thing they asked for is
   * the complaint that rule exists to answer.
   */
  {
    path: APP_PATH.REVIEWS,
    loadChildren: () => import('./features/reviews/reviews.routes').then((m) => m.REVIEWS_ROUTES),
  },
  /*
   * A partner's published page, at the address the editor gives them.
   *
   * ⚠ ONE MODULE, FOUR ADDRESSES, BECAUSE THE PARTNER HAS ALREADY BEEN GIVEN THE ADDRESS. The
   * editor prints `/doctor/<name>` or `/hospital/<name>` and a clinic puts that on a board and on a
   * prescription pad, so all four kinds have to resolve here. The kind is not used to look the page
   * up — a slug is unique across every kind — but an address that 404s because the site only knew
   * about one of them would be the partner's problem and our fault.
   *
   * ⚠ THEY SIT ABOVE THE WILDCARD. Below it, every one of them would silently redirect to /plans.
   */
  {
    path: APP_PATH.PAGE_DOCTOR,
    loadChildren: () => import('./features/partner-page/partner-page.routes').then((m) => m.PARTNER_PAGE_ROUTES),
  },
  {
    path: APP_PATH.PAGE_HOSPITAL,
    loadChildren: () => import('./features/partner-page/partner-page.routes').then((m) => m.PARTNER_PAGE_ROUTES),
  },
  {
    path: APP_PATH.PAGE_LABORATORY,
    loadChildren: () => import('./features/partner-page/partner-page.routes').then((m) => m.PARTNER_PAGE_ROUTES),
  },
  {
    path: APP_PATH.PAGE_RADIO,
    loadChildren: () => import('./features/partner-page/partner-page.routes').then((m) => m.PARTNER_PAGE_ROUTES),
  },
  { path: APP_PATH.LEGAL, loadChildren: () => import('./features/legal/legal.routes').then((m) => m.LEGAL_ROUTES) },
  { path: '**', redirectTo: APP_PATH.PLANS },
];
